import i18n from "./i18n.ts";
import type { HighlightCardData } from "./highlightCard.ts";

// N4-1 "重磅" (headline) thresholds. A regular signing / trade keeps its N4-2 L1
// success toast; a headline one is promoted to an L2 highlight card *instead*
// (never both — the two channels must not stack for the same action). The bars
// are deliberately strict: an L2 card that fires on routine moves would train
// the user to ignore it, so only genuinely franchise-altering moves qualify.

// Player overall rating that reads as a bona-fide star. BBGM ovr sits ~48 for a
// league-average player, ~55-65 for stars/all-stars, 65+ for superstars, so 60
// captures clear star-caliber signings without catching rotation upgrades.
export const STAR_OVR = 60;

// A trade is "重磅" when it swings the *user* team's overall rating by at least
// this much. Only star-caliber players move a full team ovr by ~2+, so this is a
// clean, unit-free proxy for "a star changed hands" using data already in the
// trade summary (ovrBefore / ovrAfter). Star-for-star swaps with a ~0 net swing
// intentionally fall through to the toast rather than risk over-firing.
export const BIG_TRADE_OVR_SWING = 2;

export const isBigSigning = (ovr: number): boolean => ovr >= STAR_OVR;

export const isBigTrade = ({
	userOvrBefore,
	userOvrAfter,
}: {
	userOvrBefore: number;
	userOvrAfter: number;
}): boolean => Math.abs(userOvrAfter - userOvrBefore) >= BIG_TRADE_OVR_SWING;

// L2 highlight-card body for a headline signing / re-signing. Mirrors the toast
// wording but framed as a headline (the card supplies the "Big signing" title).
export const getSigningHighlightBody = ({
	resigning,
	playerName,
	years,
	amountText,
}: {
	resigning: boolean;
	playerName: string;
	years: number;
	amountText: string;
}): string =>
	resigning
		? i18n.t("feedbackHighlight.reSigningBody", {
				ns: "ui",
				defaultValue: "Re-signed {{name}} to a {{years}}-yr, {{amount}}/yr deal",
				name: playerName,
				years,
				amount: amountText,
			})
		: i18n.t("feedbackHighlight.signingBody", {
				ns: "ui",
				defaultValue: "Signed {{name}} to a {{years}}-yr, {{amount}}/yr deal",
				name: playerName,
				years,
				amount: amountText,
			});

// L2 highlight-card body for a headline trade.
export const getTradeHighlightBody = ({
	partnerName,
	acquiredNames,
}: {
	partnerName: string;
	acquiredNames: string[];
}): string =>
	i18n.t("feedbackHighlight.tradeBody", {
		ns: "ui",
		defaultValue: "Acquired {{players}} from the {{team}}",
		team: partnerName,
		players: acquiredNames.join(", "),
	});

// Complete L2 card for a headline signing (title + localized body + link). The
// body is plain text (no anchors), so htmlIsSafe is left false so SafeHtml
// escapes it. These UI-originated highlights go straight to emitHighlightCard —
// no server logEvent — since there is no engine event to save.
export const getSigningHighlightCard = ({
	resigning,
	playerName,
	years,
	amountText,
	url,
}: {
	resigning: boolean;
	playerName: string;
	years: number;
	amountText: string;
	url?: string;
}): Omit<HighlightCardData, "id"> => ({
	title: i18n.t("feedbackHighlight.signingTitle", {
		ns: "ui",
		defaultValue: "Big signing",
	}),
	message: getSigningHighlightBody({ resigning, playerName, years, amountText }),
	url,
});

// Complete L2 card for a headline trade.
export const getTradeHighlightCard = ({
	partnerName,
	acquiredNames,
	url,
}: {
	partnerName: string;
	acquiredNames: string[];
	url?: string;
}): Omit<HighlightCardData, "id"> => ({
	title: i18n.t("feedbackHighlight.tradeTitle", {
		ns: "ui",
		defaultValue: "Blockbuster trade",
	}),
	message: getTradeHighlightBody({ partnerName, acquiredNames }),
	url,
});
